'use client';
import { useEffect } from 'react';

import { useAtomValue } from 'jotai';
import Image from 'next/image';

import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { generateSrcSet } from '@core/Components/HeycarImage/GenerateSrcSet';
import { HeycarImage } from '@core/Components/HeycarImage/HeycarImage';
import { heycarLoader } from '@core/Components/HeycarImage/HeycarLoader';
import { IMAGE_SIZES, SRC_SET_WIDTHS } from '@core/Constants/ImageCarousel.constants';
import { PAGE_TYPE } from '@core/Constants/PageType';
import { debounce } from '@core/Utils/Debounce';
import { getImgLoader } from '@core/Utils/Image/GetImgLoader';
import RacLogo from '@gds/RacBadge/rac.webp';

import { FavouritesButton } from '../FavouritesButton/FavouritesButton';
import { purchaseProLoader } from '../PpImage/Utils/PurchaseProLoader';
import { ShareButton } from '../ShareButton/ShareButton';
import type { ImageCarouselProps } from './ImageCarousel.entity';
import ImageCarouselDots from './ImageCarouselDots/ImageCarouselDots';

import styles from './ImageCarousel.module.css';

export const ImageCarousel = ({
  images,
  imageOnClick,
  positionIndex,
  index,
  strings,
  visibleOverflowView,
  shouldShowDotCarousel = true,
  size = 'large',
  carouselProps,
  shouldShowFavIcon = false,
  shouldShowShare = false,
  isRacApproved = false,
  isPdp = false,
  isStockUnavailable = false,
  tenant,
  comingSoonSlot,
}: ImageCarouselProps) => {
  const totalImgs = images.length;
  const hasMultipleImages = totalImgs > 1;
  const vehicle = useAtomValue(currentVehicleAtom);
  const { currentIndex, dragStart, dragging, dragEnd, moveTo, fromIndex, toIndex, carouselRef } =
    carouselProps;

  let lastWindowWidth = typeof window === 'undefined' ? 0 : window.innerWidth;

  function handleResize() {
    const windowWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
    const threshold = 1;
    if (Math.abs(windowWidth - lastWindowWidth) > threshold) {
      lastWindowWidth = windowWidth;
      debounce(() => moveTo(currentIndex), 500)();
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [moveTo, currentIndex]);

  useEffect(() => {
    if (typeof positionIndex === 'number') moveTo(positionIndex);
  }, [positionIndex]);

  const logoSrc = isRacApproved ? RacLogo : null;
  const logoAlt = isRacApproved ? 'rac approved logo' : '';
  const defaultLoader = tenant === 'purchasePro' ? purchaseProLoader : heycarLoader;

  return (
    <section
      className={`${styles.wrapper} ${visibleOverflowView ? styles.visibleOverflow : ''}`}
      role="group"
      aria-roledescription="carousel"
      aria-label={strings.carouselLabel}
      data-test-id="image-carousel"
      data-size={size}
      onTouchStart={e => dragStart(e)}
      onTouchMove={e => dragging(e)}
      onTouchEnd={dragEnd}
      data-tenant={tenant}
    >
      {shouldShowShare && !isStockUnavailable && (
        <div className={`${styles.btn} ${styles.shareBtn}`}>
          <ShareButton />
        </div>
      )}
      {vehicle && shouldShowFavIcon && !isStockUnavailable && (
        <div className={`${styles.btn} ${styles.favBtn}`}>
          <FavouritesButton
            genericVehicleListings={vehicle}
            pageType={PAGE_TYPE.Pdp}
            tenant={tenant}
          />
        </div>
      )}
      <div
        className={styles.images}
        data-reservedstatus={isStockUnavailable}
        data-test-id="image-carousel-images"
        ref={carouselRef}
      >
        {images.map((image, i) => {
          if (image.isFallback) return null;
          const loadr = getImgLoader(image, defaultLoader);
          const imageWidths = SRC_SET_WIDTHS[size];
          const preloadWidth = imageWidths?.[0] ?? 565;
          if (i >= Math.max(0, currentIndex - 1) && i <= currentIndex + 2)
            return (
              <link
                key={`${image.url}-${i + 1}`}
                rel="preload"
                as="image"
                fetchPriority="low"
                href={loadr({ src: image.url, width: preloadWidth })}
                imageSrcSet={generateSrcSet(image.url, loadr, imageWidths)}
                imageSizes={IMAGE_SIZES[size]}
              />
            );
        })}

        {images.map((image, i) => {
          const loadr = getImgLoader(image, defaultLoader);
          if (i <= currentIndex + 2)
            return (
              <div
                key={`${image.url || 'fallback'}-${i + 1}`}
                className={`${styles.imageSlide} ${i === currentIndex ? 'current' : ''}`}
                role="group"
                aria-label={`${strings.slide} ${i + 1} ${strings.of} ${totalImgs}`}
                onClick={() => {
                  imageOnClick?.(i);
                }}
                data-test-id={`image-carousel-image-${i + 1}`}
              >
                {image.isFallback ? (
                  comingSoonSlot
                ) : (
                  <HeycarImage
                    src={image.url}
                    alt={image.altText || ''}
                    className={styles.image}
                    sizes={IMAGE_SIZES[size]}
                    srcSetWidths={SRC_SET_WIDTHS[size]}
                    loader={loadr}
                    forceVisible={(index ?? 100) <= 1 && i === 0}
                    preload={i <= 1}
                  />
                )}
              </div>
            );
        })}
      </div>
      {logoSrc && (
        <aside className={styles.racBadge}>
          <Image src={logoSrc} width={isPdp ? 105 : 70} height={isPdp ? 105 : 70} alt={logoAlt} />
        </aside>
      )}
      {hasMultipleImages && !isStockUnavailable && (
        <ImageCarouselDots
          strings={strings}
          moveTo={moveTo}
          totalImgs={totalImgs}
          currentIndex={currentIndex}
          fromIndex={fromIndex}
          toIndex={toIndex}
          size={size}
          shouldShowDotCarousel={shouldShowDotCarousel}
        />
      )}
    </section>
  );
};
